<template>
    <div class="wrap wrap--min">
        <SectionTitle text="Brands" class="title" />

        <!-- アンカー：A-Z + # -->
        <ul class="anchor">
            <li v-for="anchor in brandAnchors" :key="anchor.link">
                <a :href="'#' + anchor.link" v-smooth-scroll>{{ anchor.text }}</a>
            </li>
        </ul>

        <!-- <div class="search-wrap">
            <input type="text" placeholder="Search for brands ..." class="search" v-model="searchText">
            <div class="search-icon"></div>
        </div>

        <div v-show="notFound" class="notFound">
            「{{ searchText }}」に一致するブランドが見つかりませんでした。
        </div> -->

        <!-- A-Z・# グループ -->
        <ul class="brand-block">
            <li class="brand-block-row" v-for="(brandRow, index) in brands" :key="index" v-show="!brandRow.itemEmpty">
                <section>
                    <h3 class="brand-block-head" :id="brandRow.link">{{ brandRow.text }}</h3>

                    <ul class="brand-list">
                        <li class="brand-list-item" v-for="(b, i) in brandRow.contents" :key="(b.eng || b.jp || 'brand') + i" v-show="b.display">
                            <a :href="b[$store.state.hostName] || ''">
                                <!-- <img :src="b.imageURL" alt="" class="brand-list-img"> -->
                                {{ b.eng }}
                            </a>
                        </li>
                    </ul>
                </section>
            </li>
        </ul>
    </div>
</template>

<script>
import SectionTitle from '@/components/SectionTitle.vue'

export default {
  name: 'Brands',
  components: {
    SectionTitle
  },

  data () {
    return {
      searchText: '',
      brands: []
    }
  },

  computed: {
    brandOriginalJson () {
      return this.$store.state.json.brand
    },

    /**
     * アンカー：A-Z + #
     */
    brandAnchors () {
      return this.brands.map(r => ({
        text: r.text,
        link: r.link
      }))
    },

    /**
     * 全グループが空なら notFound
     */
    notFound () {
      if (!this.brands.length) return false
      return this.brands.every(r => r.itemEmpty)
    }
  },

  watch: {
    brandOriginalJson () {
      this.initBrands()
    },

    /**
     * 検索フィルタ
     */
    searchText () {
      let key = this.$_.toLower(this.searchText)
      key = this.hiraToKana(key)

      this.brands.forEach(row => {
        let empty = true
        row.contents.forEach(b => {
          const eng = this.$_.toLower(b.eng || '')
          const jp = this.hiraToKana(b.jp || '')
          b.display = eng.includes(key) || jp.includes(key)
          if (b.display) empty = false
        })
        row.itemEmpty = empty
      })
    }
  },

  methods: {
    /**
     * 平仮名 → 片仮名（検索用）
     */
    hiraToKana (str) {
      if (!str) return ''
      return str.replace(/[\u3041-\u3096]/g, m =>
        String.fromCharCode(m.charCodeAt(0) + 0x60)
      )
    },

    /**
     * A-Z 判定（それ以外は #）
     */
    getAZKey (obj) {
      const eng = (obj.eng || '').trim()
      const jp = (obj.jp || '').trim()
      const src = eng || jp
      if (!src) return '#'

      const first = src.charAt(0)
      return /^[a-zA-Z]$/.test(first) ? first.toUpperCase() : '#'
    },

    /**
     * A-Z + # グループ生成
     */
    buildGroups () {
      const groups = []

      for (let c = 65; c <= 90; c++) {
        const letter = String.fromCharCode(c)
        groups.push({
          text: letter,
          key: letter,
          link: letter.toLowerCase(),
          contents: [],
          itemEmpty: false
        })
      }

      groups.push({
        text: '#',
        key: '#',
        link: 'other',
        contents: [],
        itemEmpty: false
      })

      return groups
    },

    /**
     * 重複判定用キー
     * - eng（優先）→ jp
     * - 小文字化、前後空白除去、連続空白を単一化
     */
    getDedupeKey (obj) {
      const base = (obj.eng || obj.jp || '').toString()
      const normalized = base
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')

      // 万一 name が空なら imageURL や各リンクも使って衝突を避ける
      if (normalized) return normalized

      const img = (obj.imageURL || '').toString().trim()
      if (img) return `img:${img}`

      // 最終手段：代表URL
      const url = (obj.R1 || obj.R2 || obj.Y1 || obj.Y2 || obj.O || obj.Q || obj.W || '').toString().trim()
      return url ? `url:${url}` : `unknown:${Math.random().toString(36).slice(2)}`
    },

    /**
     * 2件をマージ（先勝ち + 欠損補完）
     */
    mergeBrand (base, incoming) {
      const hosts = ['R1', 'R2', 'Y1', 'Y2', 'O', 'Q', 'W']

      // 表示制御は検索が握るので基本は true
      base.display = true

      // 代表値（先勝ち。ただし空なら補完）
      if (!base.eng && incoming.eng) base.eng = incoming.eng
      if (!base.jp && incoming.jp) base.jp = incoming.jp
      if (!base.imageURL && incoming.imageURL) base.imageURL = incoming.imageURL

      // host別URLを欠損補完
      hosts.forEach(k => {
        if (!base[k] && incoming[k]) base[k] = incoming[k]
      })

      return base
    },

    /**
     * JSON → 重複統合 → A-Z・# に再編成
     */
    initBrands () {
      const source = this.brandOriginalJson || []
      const groups = this.buildGroups()

      // 1) 重複統合
      const map = new Map()
      source.forEach(obj => {
        const key = this.getDedupeKey(obj)
        if (!map.has(key)) {
          // 参照をそのまま使うと他処理と干渉する場合があるので shallow copy 推奨
          const copied = { ...obj }
          copied.display = true
          map.set(key, copied)
        } else {
          const merged = this.mergeBrand(map.get(key), obj)
          map.set(key, merged)
        }
      })

      const deduped = Array.from(map.values())

      // 2) グルーピング
      deduped.forEach(obj => {
        obj.display = true
        const key = this.getAZKey(obj)
        const target = groups.find(g => g.key === key)
        if (target) target.contents.push(obj)
      })

      // 3) 各グループ内を並び替え（eng優先→jp）
      //   groups.forEach(g => {
      //     g.contents.sort((a, b) => {
      //       const ak = ((a.eng || a.jp || '') + '').toUpperCase()
      //       const bk = ((b.eng || b.jp || '') + '').toUpperCase()
      //       return ak.localeCompare(bk)
      //     })
      //   })

      // 4) 空グループは非表示対象に
      groups.forEach(g => {
        g.itemEmpty = g.contents.length === 0
      })

      this.brands = groups
    }
  },

  mounted () {
    this.initBrands()
  }
}
</script>

<style lang="scss" scoped>
.anchor {
    display: grid;
    grid-template-columns: repeat(27, 1fr);
    padding: 10px;
    font-size: 18px;
    background-color: #dddddd;

    @media screen and (max-width: 722px) {
        overflow-x: scroll;
        overflow-y: hidden;
        font-size: 16px;
        white-space: nowrap;
    }

    @media screen and (min-width: 723px) and (max-width: 963px) {
        overflow-x: scroll;
        overflow-y: hidden;
        font-size: 18px;
        white-space: nowrap;
    }

    li {
        box-sizing: border-box;
        text-align: center;

        a {
            display: block;
            padding: 10px;
            font-family: Helvetica, Arial, sans-serif;
            font-weight: 700;
        }
    }
}

.search-wrap {
    position: relative;
}

.search {
    box-sizing: border-box;
    width: 100%;
    padding: 4px 8px;
    font-size: 16px;
    border-bottom: 1px solid #dddddd;
}

.search-icon {
    position: absolute;
    top: 4px;
    right: 2px;
    width: 40px;
    height: 26px;
    background-image: url(../assets/search.svg);
    background-repeat: no-repeat;
    background-position: top 3px right 6px;
    background-size: 20px auto;
    opacity: .3;
}

.notFound {
    margin-top: 20px;
    font-size: 14px;
    text-align: center;
}

.brand-block {
    line-height: 1.5;
    font-weight: 700;
}

.brand-block-row {
    padding-top: 80px;
}

.brand-block-row + .brand-block-row {
    padding-top: 80px;
}

.brand-block-head {
    font-size: 18px;
    font-weight: 700;
}

.brand-list {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    padding-top: 20px;

    @media screen and (max-width: 722px) {
        grid-template-columns: repeat(1, 1fr);
        gap: 16px;
        padding-top: 16px;
    }

    @media screen and (min-width: 723px) and (max-width: 963px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        padding-top: 20px;
    }
}

.brand-list-item {
    a {
        display: block;
        text-transform: uppercase;
    }
}

.brand-list-img {
    width: 65%;
}
</style>
